import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { RoleBadge } from "@/components/RoleBadge";
import { BrandHeader } from "@/components/BrandHeader";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import api from "@/lib/api";
import { Inbox, AtSign, Settings, Shield, LogOut, Crown, ArrowRight, Users, Building2, Tag, Bookmark, Menu } from "lucide-react";

// Primary nav
const primaryNav = [
  { path: "/inbox", icon: Inbox, label: "Inbox" },
];

// Inbox management tools
const inboxToolsNav = [
  { path: "/aliases", icon: AtSign, label: "Aliases" },
  { path: "/categories", icon: Tag, label: "Categories" },
  { path: "/labels", icon: Bookmark, label: "Labels" },
];

// Secondary nav
const secondaryNav = [
  { path: "/contacts", icon: Users, label: "Contacts" },
  { path: "/team", icon: Building2, label: "Team" },
];

// Bottom nav
const bottomNav = [
  { path: "/settings", icon: Settings, label: "Settings" },
];

function UserIdentityBlock({ name, email, testId }) {
  const atIdx = email ? email.lastIndexOf("@") : -1;
  const localPart = atIdx > 0 ? email.slice(0, atIdx) : (email || "");
  const domain = atIdx > 0 ? email.slice(atIdx + 1) : "";
  const roleLabel = name || localPart || "account";
  return (
    <div className="min-w-0 flex-1" data-testid={testId} title={email || undefined}>
      {/* Line 1: role/name + domain badge */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className="text-sm font-medium text-foreground truncate min-w-0"
          data-testid={testId ? `${testId}-role` : undefined}
        >
          {roleLabel}
        </span>
        {domain && (
          <span
            className="shrink-0 inline-flex items-center rounded-md bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-mono font-medium leading-none max-w-[55%] truncate"
            data-testid={testId ? `${testId}-domain` : undefined}
          >
            {domain}
          </span>
        )}
      </div>
      {/* Line 2: username / local part only */}
      {localPart && (
        <p
          className="text-[11px] text-muted-foreground truncate leading-tight mt-0.5"
          data-testid={testId ? `${testId}-username` : undefined}
        >
          {localPart}
        </p>
      )}
    </div>
  );
}

function SidebarContents({ user, logout, isAdmin, isPro, usage, isImpersonating, onNavigate }) {
  const adminPrimaryNav = [{ path: "/inbox", icon: Inbox, label: "Admin Inbox" }];
  const handleNav = () => { if (onNavigate) onNavigate(); };

  return (
    <>
      <BrandHeader
        role={isPro && !isAdmin ? "pro" : user?.role}
        subtitle={isAdmin ? "administrative account" : "anonymous relay"}
      />
      <nav className="flex-1 px-3 pt-2 overflow-y-auto">
        {/* Primary */}
        <div className="space-y-0.5">
          {(isAdmin ? adminPrimaryNav : primaryNav).map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              onClick={handleNav}
              data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#F5F5F5] text-[#111111] font-medium" : "text-[#555555] hover:bg-[#F7F7F7] hover:text-[#111111]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </div>

        {/* Divider + Inbox Management */}
        <div className="my-3 mx-3 border-t border-[#E5E5E5]" />
        <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#888888]">Inbox tools</p>
        <div className="space-y-0.5">
          {inboxToolsNav.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              onClick={handleNav}
              data-testid={`sidebar-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#F5F5F5] text-[#111111] font-medium" : "text-[#555555] hover:bg-[#F7F7F7] hover:text-[#111111]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </div>

        {/* Divider + Secondary */}
        <div className="my-3 mx-3 border-t border-[#E5E5E5]" />
        <div className="space-y-0.5">
          {secondaryNav.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              onClick={handleNav}
              data-testid={`sidebar-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#F5F5F5] text-[#111111] font-medium" : "text-[#555555] hover:bg-[#F7F7F7] hover:text-[#111111]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </div>

        {/* Divider + Bottom */}
        <div className="my-3 mx-3 border-t border-[#E5E5E5]" />
        <div className="space-y-0.5">
          {bottomNav.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              onClick={handleNav}
              data-testid={`sidebar-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive ? "bg-[#F5F5F5] text-[#111111] font-medium border-l-2 border-violet-500 pl-2.5" : "text-[#555555] hover:bg-[#F5F5F5] hover:text-[#111111]"
                }`
              }
            >
              <Icon size={18} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Usage + Upgrade CTA (Free users) */}
      {!isPro && (
        <div className="mx-3 mb-3" data-testid="sidebar-upgrade-cta">
          <Link to="/pricing" onClick={handleNav}>
            <div className="p-3 rounded-lg bg-violet-50 border border-violet-200 hover:border-violet-500/30 transition-colors cursor-pointer space-y-2">
              <div className="flex items-center gap-2">
                <Crown size={14} className="text-violet-600" />
                <span className="text-xs font-medium text-violet-600">Upgrade to Pro</span>
              </div>

              {/* Usage bar */}
              {usage && usage.limit && (
                <div data-testid="sidebar-usage">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono text-violet-600">{usage.used}/{usage.limit} alias{usage.limit > 1 ? "es" : ""}</span>
                    {usage.used >= usage.limit && (
                      <span className="text-[9px] font-mono text-amber-600 font-medium">LIMIT</span>
                    )}
                  </div>
                  <div className="h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${usage.used >= usage.limit ? "bg-amber-500" : "bg-violet-500"}`}
                      style={{ width: `${Math.min((usage.used / usage.limit) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <p className="text-[10px] text-violet-600">Unlimited aliases, categories & more</p>
            </div>
          </Link>
        </div>
      )}

      <div className="p-4 border-t border-[#E5E5E5]">
        {/* Note: the "Admin Panel" shortcut previously rendered here has been
            removed. Context switching now lives in the always-visible
            MobileAdminSwitch (bottom bar on mobile) and the top "Admin Inbox"
            ribbon on desktop — avoiding duplicate navigation paths. */}
        {isImpersonating ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-xs font-medium text-amber-600 shrink-0">
                {user?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <UserIdentityBlock name={user?.name} email={user?.email} testId="sidebar-user-identity" />
                <p className="text-[10px] text-amber-600 font-medium mt-0.5">Viewing as user</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={() => {
                sessionStorage.removeItem("impersonate_token");
                sessionStorage.removeItem("impersonate_active");
                window.close();
              }}
              data-testid="exit-impersonation-sidebar"
            >
              <ArrowRight size={15} className="mr-2 rotate-180" /> Exit User View
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-xs font-medium text-[#555555] shrink-0">
                {user?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <UserIdentityBlock name={user?.name} email={user?.email} testId="sidebar-user-identity" />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-[#555555] hover:text-[#111111] hover:bg-[#F5F5F5]"
              onClick={logout}
              data-testid="sidebar-logout-button"
            >
              <LogOut size={16} className="mr-2" /> Sign out
            </Button>
          </>
        )}
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const [planStatus, setPlanStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/payments/plan-status").catch(() => null),
      api.get("/aliases/usage").catch(() => null),
    ]).then(([planRes, usageRes]) => {
      if (planRes) setPlanStatus(planRes.data);
      if (usageRes) setUsage(usageRes.data);
    });
  }, []);

  // Auto-close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isPro = planStatus?.is_pro;
  const isImpersonating = typeof window !== "undefined" && sessionStorage.getItem("impersonate_active") === "true";

  const sidebarProps = { user, logout, isAdmin, isPro, usage, isImpersonating };

  // InboxPage renders its own mobile header (title + alias context + hamburger) so we skip the
  // generic Layout top bar there to avoid stacking two bars and crowding the alias context.
  const isInboxRoute = location.pathname === "/inbox" || location.pathname.startsWith("/inbox/");

  return (
    <div className={`light-theme h-screen flex flex-col md:flex-row overflow-hidden bg-white ${isImpersonating ? "pt-8" : ""}`} data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-[#E5E5E5] bg-white shrink-0" data-testid="desktop-sidebar">
        <SidebarContents {...sidebarProps} />
      </aside>

      {/* Mobile slide-in drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 md:hidden flex flex-col bg-white" data-testid="app-sidebar-drawer">
          <SidebarContents {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content column
          Bottom padding matches whichever fixed nav is rendered below:
          • admins → MobileAdminSwitch (h-14 = 56px) → pb-14
          • everyone else → legacy mobile-bottom-nav  (h-16 = 64px) → pb-16
          Mismatch was causing a ~8px "floating" gap between content and the
          admin switch on /inbox. Keeping both navs and their respective
          paddings aligned removes that gap. */}
      <main className={`flex-1 overflow-hidden flex flex-col min-w-0 md:pb-0 ${isAdmin ? "pb-14" : "pb-16"}`}>
        {/* Mobile top bar — global app nav entry point (hidden on /inbox which has its own header) */}
        {!isInboxRoute && (
          <div className="md:hidden flex items-center justify-between h-12 px-3 border-b border-[#E5E5E5] bg-white shrink-0" data-testid="app-mobile-topbar">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-[#555555]"
                  data-testid="app-mobile-menu-toggle"
                  aria-label="Open menu"
                >
                  <Menu size={18} />
                </Button>
              </SheetTrigger>
            </Sheet>
            <div className="text-sm font-medium text-[#111111] capitalize" data-testid="app-mobile-title">
              {(location.pathname.replace("/", "") || "Inbox").split("/")[0]}
            </div>
            <RoleBadge role={isPro && !isAdmin ? "pro" : user?.role} />
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation — admins use the MobileAdminSwitch (mounted
          by InboxPage) instead, so this regular-user tab bar is hidden for
          admins to avoid two stacked bottom bars. Admins can still reach
          Aliases/Settings via the sidebar drawer. */}
      {!isAdmin && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-t border-[#E5E5E5] z-50 flex justify-around items-center px-2 pb-safe" data-testid="mobile-bottom-nav">
          {[...primaryNav, ...inboxToolsNav.slice(0, 1), ...bottomNav].map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path;
            return (
              <NavLink
                key={path}
                to={path}
                data-testid={`mobile-nav-${label.toLowerCase()}`}
                className={`flex flex-col items-center gap-0.5 py-1 px-3 min-w-[56px] transition-colors duration-200 ${
                  isActive ? "text-violet-600" : "text-[#888888]"
                }`}
              >
                <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                <span className="text-[10px] font-medium">{label}</span>
              </NavLink>
            );
          })}
        </nav>
      )}
    </div>
  );
}
